import { computed, inject, Injector, signal } from "@angular/core";
import { CommonDateTimeField, CommonDateTimeFieldConfig, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField, CommonUrlField, CommonUrlFieldConfig, UtilityService } from "@eforall/common";
import { DialogService } from "@eforall/dashboard";
import { EventDialogFuncData } from "@interfaces";
import { PersonDialog } from "../../../../../ui/widget/person-dialog";
import { EventDialogDataService } from "../event-dialog-data.service";

export interface EventEditorFormValue {
	startUTC: number,
	endUTC: number,
	toolSelection?: 'automatic' | 'custom',
	toolStartUTC?: number,
	toolEndUTC?: number,
	link?: string,
	publicNotes?: string,
	venue: 'In Person' | 'Online',
	venueDisplayedName?: string,
	venueId?: number,
}

export const MINUTE = 60;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export class EventEditorFormService {

	public readonly form = new CommonForm<EventEditorFormValue>(inject(Injector));
	public readonly dialogService = inject(DialogService);
	public readonly eventDialogDataService = inject(EventDialogDataService);
	public readonly util = inject(UtilityService);
	public values = signal<EventEditorFormValue>({
		link: undefined,
		publicNotes: undefined,
		startUTC: this.util.date.toUTC(new Date())!,
		endUTC: this.util.date.toUTC(new Date())!,
		toolStartUTC: undefined,
		toolEndUTC: undefined,
		venue: 'Online',
	});

	public hostPerson = signal<{ personId: number, fullName: string }>({ personId: 0, fullName: '' });

	public setValues(event?: EventDialogFuncData) {
		// Reset form state to ensure pending values are set to null before setting new values
		this.form.resetAll();
		if (!event) {
			this.values.set({
				link: undefined, publicNotes: undefined,
				startUTC: this.util.date.toUTC(new Date())!,
				endUTC: this.util.date.toUTC(new Date())!,
				toolStartUTC: undefined,
				toolEndUTC: undefined,
				venueId: undefined,
				venue: 'Online'
			});
			this.hostPerson.set({ personId: 0, fullName: '' });
			return;
		}
		const { hostPersonId, link, publicNotes, startUTC, endUTC, toolEndUTC, toolStartUTC, venue, hostFullName } = event;

		const hasDefaultToolDates = this.hasDefaultToolDates(startUTC, endUTC, toolStartUTC, toolEndUTC);
		this.values.set({ link, publicNotes, startUTC, endUTC, toolEndUTC, toolStartUTC, venueId: venue?.venueId, venue: venue ? 'In Person' : 'Online', venueDisplayedName: venue?.displayedName, toolSelection: hasDefaultToolDates ? 'automatic' : 'custom' });
		this.hostPerson.set({ personId: hostPersonId, fullName: hostFullName });
	}



	readonly startDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({ label: 'Start', isRequired: true, isSpanish: false, isEnabled: computed(() => this.eventDialogDataService.canEdit()), type: 'event', includeTime: true }),
		actualValue: computed(() => this.values().startUTC),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'startUTC',
		form: this.form,
	}));

	endDateMin = computed(() => this.startDate().pendingValue() || this.startDate().actualValue());
	readonly endDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'End',
			minDateUTC: this.endDateMin() ? this.endDateMin()! + 1 * 60 : this.endDateMin(),
			isRequired: true, isSpanish: false, type: 'event', includeTime: true, isEnabled: computed(() => this.eventDialogDataService.canEdit())
		}),
		actualValue: computed(() => this.values().endUTC),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'endUTC',
		form: this.form,
	}));


	private readonly toolOptions = computed(() => [
		{ text: "24 hours before event to 24 hours after", value: "automatic" },
		{ text: "Custom", value: "custom" },
	]);

	public readonly toolEnabledSelection = computed<CommonSelectField<string | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: '',
			options: this.toolOptions(),
			isRequired: !!this.eventDialogDataService.event()?.eventType.toolNameLabelKey,
			isSpanish: false,
			isEnabled: computed(() => this.eventDialogDataService.canEdit()),
		}),
		actualValue: computed(() => this.values().toolSelection),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'toolSelection',
		form: this.form,
	}));

	public toolSelectionValue = computed(() => this.toolEnabledSelection().pendingValue() || this.toolEnabledSelection().actualValue());

	readonly toolStartDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'Tool Start',
			isRequired: !!this.eventDialogDataService.event()?.eventType.toolNameLabelKey,
			isSpanish: false,
			isEnabled: computed(() => this.eventDialogDataService.canEdit()),
			type: 'event',
			includeTime: true,

		}),
		actualValue: computed(() => this.values().toolStartUTC),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'toolStartUTC',
		form: this.form,
	}));

	toolEndDateMin = computed(() => this.toolStartDate().pendingValue() || this.toolStartDate().actualValue());
	readonly toolEndDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'Tool End',
			minDateUTC: this.toolEndDateMin() ? this.toolEndDateMin()! + 1 * 60 : this.toolEndDateMin(),
			isRequired: !!this.eventDialogDataService.event()?.eventType.toolNameLabelKey, isSpanish: false, type: 'event', includeTime: true, isEnabled: computed(() => this.eventDialogDataService.canEdit())
		}),
		actualValue: computed(() => this.values().toolEndUTC),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'toolEndUTC',
		form: this.form,
	}));


	readonly duration = computed(() => {
		const startUTC = this.startDate().pendingValue() || this.startDate().actualValue();
		const endUTC = this.endDate().pendingValue() || this.endDate().actualValue();
		return startUTC && endUTC ? this.util.date.calculateTimePeriod(startUTC, endUTC) : undefined;
	});

	readonly toolDuration = computed(() => {
		const startUTC = this.toolStartDate().pendingValue() || this.toolStartDate().actualValue();
		const endUTC = this.toolEndDate().pendingValue() || this.toolEndDate().actualValue();
		return startUTC && endUTC ? this.util.date.calculateTimePeriod(startUTC, endUTC) : undefined;
	});



	private readonly venueOptions = computed(() => [
		{
			text: 'In Person',
			value: 'In Person',
			disabled: false,
		},
		{
			text: 'Online',
			value: 'Online',
			disabled: false,
		}
	]);

	public readonly venue = computed<CommonSelectField<string>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Venue',
			options: this.venueOptions(),
			isRequired: true,
			isSpanish: false,
			isEnabled: computed(() => this.eventDialogDataService.canEdit()),
		}),
		actualValue: computed(() => this.values().venue),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'venue',
		form: this.form,
	}));

	public venueValue = computed(() => this.venue().pendingValue() || this.venue().actualValue());



	readonly publicNotes = computed<CommonTextField>(() => ({
		config: signal({ label: 'Public Notes', min: 0, max: 200, fixCase: 'sentence-case', isRequired: false, multiLine: true, isSpanish: false, isEnabled: computed(() => this.eventDialogDataService.canEdit()) }),
		actualValue: computed(() => this.values().publicNotes || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'publicNotes',
		form: this.form,
	}));


	readonly link = computed<CommonUrlField>(() => ({
		config: signal<CommonUrlFieldConfig>({
			label: 'Link',
			isRequired: false,
			isEnabled: computed(() => this.eventDialogDataService.canEdit())
		}),
		formFieldName: 'link',
		form: this.form,
		actualValue: computed(() => this.values().link ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	public handleStaffValueChange(value: { personId?: number, fullName?: string } | undefined) {
		if (value?.personId && value?.fullName) this.hostPerson.set({ personId: value.personId, fullName: value.fullName });
	};


	public async openPersonDialog() {
		const hostPerson = this.hostPerson();
		if (!hostPerson.personId) return;
		await this.dialogService.showCustom(PersonDialog, { data: { personId: hostPerson.personId, toEdit: false } }, 800, 800);
	}



	/**
	 * Determine if the tool dates are exactly one day before and
	 * one day after the event dates.
	 */
	private hasDefaultToolDates(startUTC: number, endUTC: number, toolStartUTC?: number, toolEndUTC?: number): boolean {

		const eventStart = startUTC ?? 0;
		if (!eventStart) return true;

		const eventEnd = endUTC ?? 0;
		if (!eventEnd) return true;

		const toolStart = toolStartUTC ?? 0;
		if (!toolStart) return false;

		const toolEnd = toolEndUTC ?? 0;
		if (!toolEnd) return false;

		return toolStart == (eventStart - DAY) && toolEnd == (eventEnd + DAY);

	}
}